/**
 * Normalise text that may have been pasted from a rich-text editor
 * (HubSpot, Word, Outlook, Notion, etc.). Strips HTML, decodes common
 * entities, normalises whitespace + Unicode separators, and removes
 * control characters that can later cause issues in JSON round-trips
 * or with LLM APIs.
 */

// Unicode chars that occasionally break JSON parsers and LLM clients:
const LINE_SEPARATOR = String.fromCharCode(0x2028);       // U+2028
const PARAGRAPH_SEPARATOR = String.fromCharCode(0x2029);  // U+2029
const NBSP = String.fromCharCode(0x00A0);                 // U+00A0
const NARROW_NBSP = String.fromCharCode(0x202F);          // U+202F

export function sanitizePastedText(raw: string): string {
    if (!raw) return '';
    let s = raw;

    // 1. Strip any HTML tags that slipped through (e.g. <p>, <br>, <strong>)
    s = s.replace(/<[^>]+>/g, ' ');

    // 2. Decode the most common HTML entities
    s = s
        .replace(/&nbsp;/gi, ' ')
        .replace(/&amp;/gi, '&')
        .replace(/&lt;/gi, '<')
        .replace(/&gt;/gi, '>')
        .replace(/&quot;/gi, '"')
        .replace(/&#39;/gi, "'")
        .replace(/&ldquo;|&rdquo;/gi, '"')
        .replace(/&lsquo;|&rsquo;/gi, "'")
        .replace(/&mdash;/gi, '—')
        .replace(/&ndash;/gi, '–')
        .replace(/&hellip;/gi, '…');

    // 3. Normalise line endings to LF
    s = s.replace(/\r\n?/g, '\n');

    // 4. Strip C0 / DEL control chars except \t and \n (protect JSON + LLM APIs)
    // eslint-disable-next-line no-control-regex
    s = s.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '');

    // 5. Line separator (U+2028) and paragraph separator (U+2029) — valid in
    //    JSON strings, but some parsers / LLM streaming clients stumble on them.
    s = s.split(LINE_SEPARATOR).join('\n');
    s = s.split(PARAGRAPH_SEPARATOR).join('\n\n');

    // 6. Non-breaking space (U+00A0) + narrow NBSP (U+202F) → regular space
    s = s.split(NBSP).join(' ').split(NARROW_NBSP).join(' ');

    // 7. Collapse 3+ blank lines to a single empty line
    s = s.replace(/\n{3,}/g, '\n\n');

    // 8. Trim trailing whitespace on each line
    s = s.replace(/[ \t]+(?=\n|$)/g, '');

    return s.trim();
}
